import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

interface Props {
    index: number;
    moveListItem: (from: number, to: number) => void;
    dragType: string;
    isSelfDraggable?: boolean;
}

const DndListItem: React.FC<Props> = ({ children, index, moveListItem, dragType, isSelfDraggable }) => {
    const boundingClientRectFallback = { left: 0, right: 0, top: 0, bottom: 0, height: 0, width: 0 };
    const ref = useRef<HTMLDivElement>(null);
    // useDrag - the list item is draggable
    const [, dragRef, previewRef] = useDrag({
        type: dragType,
        item: { index },
    });

    // useDrop - the list item is also a drop area
    const [, dropRef] = useDrop({
        accept: dragType,
        hover: (item: { index: number }, monitor) => {
            const dragIndex = item.index;
            const hoverIndex = index;
            const hoverBoundingRect = ref.current?.getBoundingClientRect() ?? boundingClientRectFallback;
            const hoverTopThirdY = hoverBoundingRect.height / 3;
            const hoverBottomThirdY = hoverBoundingRect.height - hoverTopThirdY;

            const cursorPositionY = monitor.getClientOffset()?.y ?? 0;
            const hoverActualY = cursorPositionY - hoverBoundingRect.top;

            // if dragging down, continue only when hover is smaller than top third Y of element it is replacing
            if (dragIndex < hoverIndex && hoverActualY < hoverTopThirdY) return;
            // if dragging up, continue only when hover is bigger than bottom third Y of element it is replacing
            if (dragIndex > hoverIndex && hoverActualY > hoverBottomThirdY) return;

            moveListItem(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    // if self draggable, give the parent both drag and drop ref - else just give it preview (hover) and drop ref
    const parentRef = (isSelfDraggable ? dragRef(dropRef(ref)) : previewRef(dropRef(ref))) as React.LegacyRef<
        HTMLDivElement
    >;

    const dragAndDropChildren = () => {
        if (isSelfDraggable) return children;
        // element is not self draggable - pass down dragRef to children as prop to use
        const childrenArray = Array.isArray(children) ? children : [children];
        return React.Children.map(childrenArray, (child: any) => {
            return React.cloneElement(child, { dragRef });
        });
    };

    return <div ref={parentRef}>{dragAndDropChildren()}</div>;
};

export default DndListItem;
